// src/pages/SelfCheckin.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  bookCheckInService,
  completeCheckIn,
  createCheckInPaymentLink,
  fetchCheckInFlow,
  fetchCheckInPaymentState,
  fetchCheckInServiceOffers,
  JOURNEY_TOKEN_STORAGE_KEY,
  redirectToPaymentLink,
  removeCheckInService,
  saveRegistrationCard,
} from "../services/reservationService";
import {
  CHECKIN_LANGUAGE_STORAGE_KEY,
  getCheckinTranslator,
  localizeKnownOption,
  normalizeCheckinLanguage,
} from "../i18n/checkin";
import {
  getStoredRoomAccess,
  isRoomAccessEstablished,
  redactRoomPinsUnlessAllowed,
  storeRoomAccess,
} from "../utils/roomAccess";
import {
  canUseCapability,
  getBookingOwnerContext,
} from "../utils/journeyAccess";
import usePaymentVerification, { isVerifiedZeroPayment } from "../hooks/usePaymentVerification";
import {
  PAYMENT_FLOW_ARRIVAL,
  readPaymentResume,
  savePaymentResume,
} from "../utils/paymentResume";
import {
  CHECKIN_PAYMENT_RETURN_STORAGE_KEY as PAYMENT_RETURN_KEY,
  clearCompletedCheckinState,
  isOnlineCheckinComplete,
} from "../utils/checkinCompletion";
import breakfastImage from "../assets/images/onlinecheckin/airport_breakfast.webp";
import earlyBirdImage from "../assets/images/onlinecheckin/earlybird.png";
import saunaImage from "../assets/images/onlinecheckin/airportsauna.png";
import babyBedImage from "../assets/images/onlinecheckin/babybed.png";
import petImage from "../assets/images/onlinecheckin/pet.png";
import parkingImage from "../assets/images/onlinecheckin/undergroundgaradge.png";
import taxiImage from "../assets/images/onlinecheckin/taxiToAirport.png";
import earlyCheckinImage from "../assets/images/onlinecheckin/earlycheckin.png";
import lateCheckoutImage from "../assets/images/onlinecheckin/latecheckout.png";
import {
  breakfastCode, breakfastSelectionError, breakfastSelectionPrice, breakfastWizardOffers,
  canBookOffer, canRemoveService, defaultServiceDetails, isBreakfastService, isIncludedOffer, isIncludedSaunaOffer, isLateCheckoutOffer, isParkingOffer, isSaunaOffer, isTaxiOffer,
  normalizeIncludedSauna, requiresWholeStay, selectedForOffer, serviceBasketSummary, serviceBookPayload,
  reservationGuestLimit, serviceCode, serviceDates, serviceIdentity, serviceKey, serviceQuantityLimit,
  serviceSelectionError,
} from "../utils/serviceBasket";

// ID verification is paused; keep its screens and camera flow for later reactivation.
const ID_VERIFICATION_ENABLED = false;

const STEP_KEYS = ["overview", "details", ...(ID_VERIFICATION_ENABLED ? ["idIntro", "idCapture", "identity"] : []),
  "purpose", "address", "review", "service", "serviceSummary"];

const COUNTRY_OPTIONS = [
  { value: "DE", label: "Deutschland" },
  { value: "AT", label: "Österreich" },
  { value: "CH", label: "Schweiz" },
  { value: "NL", label: "Niederlande" },
  { value: "FR", label: "Frankreich" },
  { value: "IT", label: "Italien" },
  { value: "ES", label: "Spanien" },
  { value: "GB", label: "Vereinigtes Königreich" },
  { value: "US", label: "Vereinigte Staaten" },
];

const DEFAULT_DOCUMENT_TYPES = [
  { value: "ID_CARD", label: "ID card" },
  { value: "PASSPORT", label: "Passport" },
  { value: "DRIVER_LICENSE", label: "Driver license" },
];

const DEFAULT_GENDERS = [
  { value: "MALE", label: "Männlich" },
  { value: "FEMALE", label: "Weiblich" },
  { value: "DIVERSE", label: "Divers" },
];

const TRAVEL_PURPOSES = [
  { value: "BUSINESS", label: "Geschäftlich" },
  { value: "LEISURE", label: "Freizeit" },
  { value: "MIXED", label: "Kombination aus beidem" },
  { value: "OTHER", label: "Sonstiges" },
];

const REQUIRED_BY_STEP = {
  details: ["phone", "nationality_country_code", "gender"],
  identity: [
    "document_type",
    "document_number",
    "document_issued_on",
    "date_of_birth",
  ],
  address: ["street_name", "zip_code", "city", "country_code"],
  review: ["terms_accepted", "privacy_accepted"],
};

const serviceErrorField = (error, offer, details) => {
  if (error === "services.licensePlateRequired") return "licensePlate";
  if (error === "services.taxiRequired") {
    const pickupDates = offer.taxiPickupDates || offer.taxi_pickup_dates || [];
    return !details.taxiPickupDate || (pickupDates.length && !pickupDates.includes(details.taxiPickupDate))
      ? "taxiPickupDate" : "taxiPickupTime";
  }
  if (error === "services.taxiQuarterHour") return "taxiPickupTime";
  if (error === "services.datesRequired") return "dates";
  if (error === "services.breakfastChildUnavailable") return "childBreakfastCount";
  return "count";
};

const makeOptionList = (values, fallback) => {
  if (!Array.isArray(values) || values.length === 0) {
    return fallback;
  }

  const options = values.map((item) => {
    if (typeof item === "string") {
      return { value: item, label: item.replaceAll("_", " ") };
    }

    if (Array.isArray(item)) {
      const [value, label] = item;
      return {
        value,
        label: label || String(value || "").replaceAll("_", " "),
      };
    }

    const value =
      item.value ||
      item.code ||
      item.id ||
      item.key ||
      item.slug ||
      item.identifier;
    const label =
      item.label ||
      item.name ||
      item.display ||
      item.display_name ||
      item.title ||
      item.text ||
      item.description ||
      value;

    return {
      value,
      label: String(label || "").replaceAll("_", " "),
    };
  }).filter((option) => option.value && option.label);

  return options.length > 0 ? options : fallback;
};

const getGuestId = (card, index) =>
  card?.guest_id || card?.guest?.id || card?.id || card?.guest?.external_id || `guest-${index + 1}`;

const getGuestName = (card, index) => {
  const guest = card?.guest || card;
  const firstName = guest?.first_name || guest?.firstname || "";
  const lastName = guest?.last_name || guest?.lastname || "";
  const name = `${firstName} ${lastName}`.trim();
  return name || `Gast ${index + 1}`;
};

const emptyToNull = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  return String(value).trim() === "" ? null : value;
};

const isMissing = (value) => typeof value === "boolean" ? !value : !String(value ?? "").trim();

const normaliseCard = (card, index) => ({
  language: normalizeCheckinLanguage(card?.language || localStorage.getItem(CHECKIN_LANGUAGE_STORAGE_KEY)),
  travel_purpose: card?.travel_purpose || "",
  email: card?.email || card?.guest_email || card?.guest?.email || "",
  phone: card?.phone || card?.phone_number || card?.guest?.phone || card?.guest?.mobile || "",
  nationality_country_code: card?.nationality_country_code || "",
  document_type: card?.document_type || "",
  document_number: card?.document_number || card?.passport_number || "",
  document_issued_on: card?.document_issued_on || "",
  date_of_birth: card?.date_of_birth || "",
  gender: card?.gender || "",
  street_name: card?.street_name || card?.street || "",
  address_line_2: card?.address_line_2 || "",
  zip_code: card?.zip_code || card?.postal_code || "",
  city: card?.city || "",
  country_code: card?.country_code || "",
  company_name: card?.company_name || "",
  company_vat_number: card?.company_vat_number || "",
  terms_accepted: Boolean(card?.terms_accepted),
  privacy_accepted: Boolean(card?.privacy_accepted),
  contact_permission: Boolean(card?.contact_permission),
  verification_method: card?.verification_method || "MANUAL",
  verification_status: card?.verification_status || "VERIFIED",
  guest_id: getGuestId(card, index),
  guest_name: getGuestName(card, index),
});

const normalizeCompanyContext = (data) => {
  const source = data?.companyContext || data?.company_context;
  if (!source || typeof source !== "object" || Array.isArray(source)) return null;

  return {
    locked: source.locked === true,
    skipTravelPurpose: source.skipTravelPurpose === true || source.skip_travel_purpose === true,
    skipCompanyInformation: source.skipCompanyInformation === true || source.skip_company_information === true,
    travelPurpose: String(source.travelPurpose || source.travel_purpose || "").trim().toUpperCase(),
    companyName: String(source.companyName || source.company_name || source.name || "").trim(),
    companyVatNumber: String(
      source.companyVatNumber || source.company_vat_number || source.vatNumber || source.vat_number || ""
    ).trim(),
  };
};

const applyCompanyContext = (card, companyContext) => {
  if (!companyContext) return card;
  return {
    ...card,
    travel_purpose: companyContext.locked
      ? "BUSINESS"
      : companyContext.travelPurpose || card.travel_purpose,
    company_name: companyContext.companyName || card.company_name,
    company_vat_number: companyContext.companyVatNumber || card.company_vat_number,
  };
};

const moneyLabel = (money, language) => {
  if (money?.amount === undefined || money?.amount === null || !money.currency) return "";
  return new Intl.NumberFormat(language === "en" ? "en-GB" : "de-DE", {
    style: "currency",
    currency: money.currency,
  }).format(Number(money.amount));
};

const paymentFrom = (data) => data?.checkout?.payment || data?.payment || {};
const paymentBypassedByBackend = (payment) => {
  const testOverride = payment?.test_override || payment?.testOverride || {};
  return payment?.required === false &&
    testOverride.enabled === true &&
    (testOverride.bypassPayment === true || testOverride.bypass_payment === true);
};
const paymentVerified = (payment) =>
  isVerifiedZeroPayment(payment) || paymentBypassedByBackend(payment);

const chargeMoney = (charge) => {
  const amount = charge?.amount;
  if (amount?.grossAmount !== undefined && amount?.grossAmount !== null) {
    return { amount: amount.grossAmount, currency: amount.currency };
  }
  return amount;
};

const isBabyBedOffer = (offer) => {
  const code = String(serviceCode(offer) || offer?.id || "").toUpperCase();
  const name = String(offer?.name || "").trim().toLowerCase();
  return /(^|-)(BBB|BABY|CRIB|COT)$/.test(code) ||
    /^(cot|crib|baby bed|baby cot|kinderbett)$/.test(name);
};

const isPetOffer = (offer) => {
  const code = String(serviceCode(offer) || offer?.id || "").toUpperCase();
  const name = String(offer?.name || "").trim().toLowerCase();
  return /(^|-)(HAUS|PET|DOG|ANIMAL)$/.test(code) ||
    /^(animal|pet|dog|haustier|hund)$/.test(name);
};

const serviceDisplayName = (offer, t) => {
  if (isIncludedSaunaOffer(offer)) return t("services.sauna");
  if (isBabyBedOffer(offer)) return t("services.babyBed");
  if (isPetOffer(offer)) return t("services.pet");
  return offer?.name || serviceCode(offer);
};

const serviceDisplayDescription = (offer) => {
  if (isIncludedSaunaOffer(offer)) return "";
  const description = String(offer?.description || "").trim();
  if (isBabyBedOffer(offer) && /^(cot|crib|baby bed|baby cot|kinderbett)$/i.test(description)) return "";
  if (isPetOffer(offer) && /^(animal|pet|dog|haustier|hund)$/i.test(description)) return "";
  return description;
};

const serviceImage = (offer) => {
  const code = String(serviceCode(offer) || offer?.id || "").toUpperCase();
  const name = String(offer?.name || "").toLowerCase();
  if (/AIRTX|PSFTX|TAXI/.test(code) || /taxi/.test(name)) return taxiImage;
  if (/PARK|GARAGE/.test(code) || /park|garage|tiefgarage/.test(name)) return parkingImage;
  if (/(^|-)(EAB|EB)$|EARLY.?BIRD/.test(code) || /early bird|frühstück.*früh/.test(name)) return earlyBirdImage;
  if (/FRST|BREAKFAST|^PSF$/.test(code) || /breakfast|frühstück|fruehstueck/.test(name)) return breakfastImage;
  if (/SAUNA|WELLNESS|SPA/.test(code) || /sauna|wellness|spa/.test(name)) return saunaImage;
  if (isBabyBedOffer(offer)) return babyBedImage;
  if (isPetOffer(offer)) return petImage;
  if (/^ECI$|EARLY.?CHECK/.test(code) || /early check.?in|früh.?check.?in/.test(name)) return earlyCheckinImage;
  if (/^LCO$|LATE.?CHECK/.test(code) || /late check.?out|spät.?check.?out/.test(name)) return lateCheckoutImage;
  return offer?.imageUrl || offer?.image_url || breakfastImage;
};

const flattenFirstMessage = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    for (const item of value) {
      const message = flattenFirstMessage(item);
      if (message) return message;
    }
    return "";
  }
  if (typeof value === "object") {
    for (const item of Object.values(value)) {
      const message = flattenFirstMessage(item);
      if (message) return message;
    }
  }
  return "";
};

const getErrorMessage = (error, t) => {
  const data = error?.response?.data;
  if (!data) {
    return error?.message || t("genericError");
  }
  if (typeof data === "string") {
    return data;
  }
  if (data.detail) {
    return data.detail;
  }
  if (data.error) {
    return data.error;
  }
  const fieldErrorMessage = flattenFirstMessage(
    Array.isArray(data.field_errors)
      ? data.field_errors.map((fieldError) => fieldError?.messages)
      : data.field_errors
  );
  if (fieldErrorMessage) {
    return fieldErrorMessage;
  }
  const errorsMessage = flattenFirstMessage(data.errors);
  if (errorsMessage) {
    return errorsMessage;
  }
  return t("genericError");
};

const isHeicFile = (file) =>
  /hei[cf]/i.test(file?.type || "") || /\.(heic|heif)$/i.test(file?.name || "");

const isAcceptedImageFile = (file) =>
  ["image/jpeg", "image/png", "image/webp"].includes(file?.type);

const loadImageFromFile = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("imageReadError"));
    };
    image.src = url;
  });

const canvasToJpegBlob = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("imageConvertError"));
        return;
      }
      resolve(blob);
    }, "image/jpeg", 0.9);
  });

const convertImageFileToJpeg = async (file) => {
  const image = await loadImageFromFile(file);
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;
  const context = canvas.getContext("2d");

  if (!context || !canvas.width || !canvas.height) {
    throw new Error("imageConvertError");
  }

  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  const blob = await canvasToJpegBlob(canvas);
  const baseName = String(file.name || "document-front").replace(/\.[^.]+$/, "");

  return new File([blob], `${baseName || "document-front"}.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
};

const prepareUploadImage = async (file) => {
  if (isAcceptedImageFile(file)) {
    return file;
  }

  if (isHeicFile(file)) {
    return convertImageFileToJpeg(file);
  }

  throw new Error("unsupportedImageFormat");
};

const getPreferredLanguageFromFlow = (data) => {
  const cardLanguage = data?.registration_cards?.find((card) => card?.language)?.language;
  const reservationLanguage =
    data?.preferred_language ||
    data?.language ||
    data?.reservation?.language ||
    data?.reservation?.preferred_language ||
    data?.reservation?.booker?.language ||
    data?.reservation?.booker?.preferred_language;

  return normalizeCheckinLanguage(
    cardLanguage ||
    reservationLanguage ||
    localStorage.getItem(CHECKIN_LANGUAGE_STORAGE_KEY)
  );
};

const SelfCheckin = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const appliedPrefillRef = useRef({});

  const reservationCode = localStorage.getItem("reservation-code");
  const initialPaymentResumeRef = useRef(undefined);
  if (initialPaymentResumeRef.current === undefined) {
    const savedResume = readPaymentResume(reservationCode);
    initialPaymentResumeRef.current = savedResume?.flow === PAYMENT_FLOW_ARRIVAL &&
      (savedResume.pending === true || savedResume.settled === true)
      ? { ...savedResume, pending: true, settled: false, startedAt: 0, attempts: 0 }
      : savedResume;
  }
  const autoCompletePaymentReturnRef = useRef(
    (initialPaymentResumeRef.current?.flow === PAYMENT_FLOW_ARRIVAL &&
      initialPaymentResumeRef.current?.returnedFromProvider === true &&
      (initialPaymentResumeRef.current?.pending === true || initialPaymentResumeRef.current?.settled === true)) ||
    sessionStorage.getItem(PAYMENT_RETURN_KEY) === localStorage.getItem(JOURNEY_TOKEN_STORAGE_KEY)
  );
  const autoCompletionStartedRef = useRef(false);
  const [step, setStep] = useState("overview");
  const [language, setLanguage] = useState(() =>
    normalizeCheckinLanguage(localStorage.getItem(CHECKIN_LANGUAGE_STORAGE_KEY))
  );
  const [flowData, setFlowData] = useState(null);
  const [companyContext, setCompanyContext] = useState(null);
  const [cards, setCards] = useState([]);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [returningChoice, setReturningChoice] = useState("");
  const [files, setFiles] = useState({});
  const [serviceOffers, setServiceOffers] = useState([]);
  const [serviceIndex, setServiceIndex] = useState(0);
  const [selectedServices, setSelectedServices] = useState([]);
  const [servicesReady, setServicesReady] = useState(false);
  const [serviceDetails, setServiceDetails] = useState({});
  const [serviceBasket, setServiceBasket] = useState({});
  const [serviceRemovals, setServiceRemovals] = useState({});
  const [uncertainService, setUncertainService] = useState(false);
  const submittingServicesRef = useRef(false);
  const [paymentData, setPaymentData] = useState(null);
  const [checkoutData, setCheckoutData] = useState(null);
  const [paymentOpened, setPaymentOpened] = useState(false);
  const paymentBypassActive = paymentBypassedByBackend(paymentData);
  const paymentConfirmed = paymentVerified(paymentData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [serviceErrorTarget, setServiceErrorTarget] = useState(null);
  const [backendErrors, setBackendErrors] = useState({});
  const [globalError, setGlobalError] = useState("");
  const [loading, setLoading] = useState(true);
  const [autoCompletingPaymentReturn, setAutoCompletingPaymentReturn] = useState(
    autoCompletePaymentReturnRef.current
  );
  const [saving, setSaving] = useState(false);
  const [cameraState, setCameraState] = useState("idle");
  const ownerCheckinAllowed = canUseCapability("onlineCheckin");

  const activeCard = activeCardIndex === 0 && returningChoice === "yes"
    ? { ...(cards[0] || {}), ...appliedPrefillRef.current }
    : cards[activeCardIndex] || {};
  const activeGuestId = activeCard.guest_id;
  const activeFiles = files[activeGuestId] || {};
  const returningPrefill = flowData?.returningGuestPrefill || flowData?.returning_guest_prefill || {};
  const t = useMemo(() => getCheckinTranslator(language), [language]);
  const companyLocked = companyContext?.locked === true;
  const skipTravelPurpose = companyLocked || companyContext?.skipTravelPurpose === true;
  const skipCompanyInformation = companyLocked || companyContext?.skipCompanyInformation === true;
  const nextStepAfterIdentity = skipTravelPurpose ? "address" : "purpose";
  const previousStepBeforeAddress = skipTravelPurpose
    ? (ID_VERIFICATION_ENABLED ? "identity" : "details")
    : "purpose";

  const verifyPaymentRequest = useCallback(() => fetchCheckInPaymentState(), []);
  const selectPayment = useCallback((data) => paymentFrom(data), []);
  const handlePaymentResponse = useCallback((checkout, payment) => {
    setPaymentData(payment);
    setCheckoutData(checkout);
  }, []);
  const handlePaymentSettled = useCallback(() => {
    const saved = readPaymentResume(reservationCode);
    const isPaymentReturn = saved?.flow === PAYMENT_FLOW_ARRIVAL ||
      sessionStorage.getItem(PAYMENT_RETURN_KEY) === localStorage.getItem(JOURNEY_TOKEN_STORAGE_KEY);
    setPaymentOpened(false);
    if (step === "payment") setGlobalError("");
    sessionStorage.removeItem(PAYMENT_RETURN_KEY);
    if (step === "payment" || isPaymentReturn) {
      initialPaymentResumeRef.current = savePaymentResume(reservationCode, {
        flow: PAYMENT_FLOW_ARRIVAL,
        step: "payment",
        pending: false,
        settled: true,
        startedAt: 0,
        attempts: 0,
      });
    }
  }, [reservationCode, step]);
  const persistPaymentProgress = useCallback(({ startedAt, attempts }) => {
    savePaymentResume(reservationCode, {
      flow: PAYMENT_FLOW_ARRIVAL,
      step: "payment",
      pending: true,
      settled: false,
      startedAt,
      attempts,
    });
  }, [reservationCode]);
  const paymentVerification = usePaymentVerification({
    active: step === "payment" && paymentOpened && !paymentConfirmed,
    scopeKey: reservationCode,
    verify: verifyPaymentRequest,
    selectPayment,
    onResponse: handlePaymentResponse,
    onSettled: handlePaymentSettled,
    onProgress: persistPaymentProgress,
    initialStartedAt: initialPaymentResumeRef.current?.startedAt,
    initialAttempts: initialPaymentResumeRef.current?.attempts,
  });
  const {
    beginRequest: beginPaymentRequest,
    acceptResponse: acceptPaymentResponse,
    restart: restartPaymentVerification,
  } = paymentVerification;

  const redirectToPostCheckInApp = useCallback(() => {
    clearCompletedCheckinState(reservationCode);
    const context = getBookingOwnerContext();

    if (context.bookerHasRoomAccess) {
      navigate("/home", { replace: true });
      return;
    }

    if (context.bookingAccessToken) {
      navigate(`/b/${encodeURIComponent(context.bookingAccessToken)}?checkin=complete`, { replace: true });
      return;
    }

    navigate("/home", { replace: true });
  }, [navigate, reservationCode]);

  const documentTypes = useMemo(
    () => makeOptionList(flowData?.options?.document_types, DEFAULT_DOCUMENT_TYPES)
      .map((option) => localizeKnownOption("documentTypes", option, language)),
    [flowData, language]
  );
  const genders = useMemo(
    () => makeOptionList(flowData?.options?.genders, DEFAULT_GENDERS)
      .map((option) => localizeKnownOption("genders", option, language)),
    [flowData, language]
  );
  const countries = useMemo(
    () => makeOptionList(flowData?.options?.countries, COUNTRY_OPTIONS)
      .map((option) => localizeKnownOption("countries", option, language)),
    [flowData, language]
  );

  const travelPurposes = useMemo(
    () => TRAVEL_PURPOSES.map((option) => localizeKnownOption("travelPurposes", option, language)),
    [language]
  );

  const handleLanguageChange = (nextLanguage) => {
    const normalizedLanguage = normalizeCheckinLanguage(nextLanguage);
    setLanguage(normalizedLanguage);
    localStorage.setItem(CHECKIN_LANGUAGE_STORAGE_KEY, normalizedLanguage);
    setCards((currentCards) => currentCards.map((card) => ({ ...card, language: normalizedLanguage })));
  };

  useEffect(() => {
    let isMounted = true;
    const loadT = getCheckinTranslator(localStorage.getItem(CHECKIN_LANGUAGE_STORAGE_KEY));

    if (!ownerCheckinAllowed) {
      setGlobalError(loadT("ownerOnly"));
      setLoading(false);
      return;
    }

    if (!reservationCode || !localStorage.getItem(JOURNEY_TOKEN_STORAGE_KEY)) {
      setGlobalError(loadT("reservationNotFound"));
      setLoading(false);
      return;
    }

    const paymentRequestId = beginPaymentRequest();
    fetchCheckInFlow()
      .then((data) => {
        if (!isMounted) {
          return;
        }

        const preferredLanguage = getPreferredLanguageFromFlow(data);
        setLanguage(preferredLanguage);
        localStorage.setItem(CHECKIN_LANGUAGE_STORAGE_KEY, preferredLanguage);

        const freshRoomAccess = data.room_access || data.roomAccess;
        if (
          isOnlineCheckinComplete(data.online_checkin || data.onlineCheckin) ||
          isRoomAccessEstablished(freshRoomAccess)
        ) {
          if (freshRoomAccess) storeRoomAccess(freshRoomAccess);
          redirectToPostCheckInApp();
          return;
        }

        const nextCompanyContext = normalizeCompanyContext(data);
        setFlowData(data);
        setCompanyContext(nextCompanyContext);
        setCards((data.registration_cards || []).map((card, index) => applyCompanyContext({
          ...normaliseCard(card, index),
          language: normalizeCheckinLanguage(card?.language || preferredLanguage),
        }, nextCompanyContext)));
        if (!autoCompletePaymentReturnRef.current) {
          acceptPaymentResponse(data, paymentRequestId);
        }

        const savedPaymentResume = readPaymentResume(data.reservation_id || reservationCode);
        const hasLegacyPaymentReturn =
          sessionStorage.getItem(PAYMENT_RETURN_KEY) === localStorage.getItem(JOURNEY_TOKEN_STORAGE_KEY);
        if (autoCompletePaymentReturnRef.current) {
          if (!savedPaymentResume && hasLegacyPaymentReturn) {
            initialPaymentResumeRef.current = savePaymentResume(data.reservation_id || reservationCode, {
              flow: PAYMENT_FLOW_ARRIVAL,
              step: "payment",
              pending: true,
            });
          }
          setStep("payment");
          setPaymentOpened(true);
        } else if (data.online_checkin?.is_complete === true) {
          setStep("payment");
        }
        const roomAccess =
          freshRoomAccess !== undefined ? storeRoomAccess(freshRoomAccess) : getStoredRoomAccess();
        if (data.reservation?.rooms) {
          const rooms = redactRoomPinsUnlessAllowed(data.reservation.rooms, roomAccess);
          localStorage.setItem("rooms", JSON.stringify(rooms));
          localStorage.removeItem("pin-code");
          window.dispatchEvent(new Event("booking-update"));
        }
      })
      .catch((error) => {
        console.error("Failed to load check-in flow", error);
        setGlobalError(getErrorMessage(error, loadT));
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
      stopCamera();
    };
  }, [acceptPaymentResponse, beginPaymentRequest, ownerCheckinAllowed, redirectToPostCheckInApp, reservationCode]);

  useEffect(() => {
    if (step === "idCapture") {
      startCamera();
    } else {
      stopCamera();
    }
  }, [step]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraState("unavailable");
      return;
    }

    setCameraState("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraState("ready");
    } catch (error) {
      console.warn("Camera permission denied or unavailable", error);
      setCameraState("denied");
    }
  };

  const updateActiveCard = (field, value) => {
    if (activeCardIndex === 0) delete appliedPrefillRef.current[field];
    setCards((currentCards) =>
      currentCards.map((card, index) =>
        index === activeCardIndex ? { ...card, [field]: value, language } : card
      )
    );
    setFieldErrors((currentErrors) => ({ ...currentErrors, [field]: "" }));
    setGlobalError("");
  };

  const validateFields = (card, fields) => {
    const errors = {};
    fields.forEach((field) => {
      const value = card[field];
      const fieldLabel = t(`fields.${field}`);
      if (isMissing(value)) {
        errors[field] = t("required", { field: fieldLabel });
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildPayload = (card) => ({
    language,
    email: card.email,
    phone: card.phone,
    travel_purpose: companyLocked ? "BUSINESS" : companyContext?.travelPurpose || card.travel_purpose,
    nationality_country_code: card.nationality_country_code,
    gender: card.gender,
    ...(ID_VERIFICATION_ENABLED ? {
      document_type: card.document_type,
      document_number: card.document_number,
      document_issued_on: emptyToNull(card.document_issued_on),
      date_of_birth: emptyToNull(card.date_of_birth),
      verification_method: card.verification_method,
      verification_status: card.verification_status,
    } : {}),
    street_name: card.street_name,
    address_line_2: card.address_line_2,
    zip_code: card.zip_code,
    city: card.city,
    country_code: card.country_code,
    ...(!skipCompanyInformation ? {
      company_name: card.company_name,
      company_vat_number: card.company_vat_number,
    } : {}),
    terms_accepted: card.terms_accepted,
    privacy_accepted: card.privacy_accepted,
    contact_permission: card.contact_permission,
  });

  const persistActiveCard = async (fields = []) => {
    if (!activeGuestId) {
      setGlobalError(t("reservationNotFound"));
      return false;
    }

    if (fields.length && !validateFields(activeCard, fields)) {
      return false;
    }

    setSaving(true);
    setGlobalError("");
    try {
      await saveRegistrationCard(activeGuestId, buildPayload(activeCard), activeFiles);
      return true;
    } catch (error) {
      console.error("Failed to save registration card", error);
      if (error?.response?.status === 409) {
        const onlineCheckin = error.response?.data?.online_checkin;
        if (!onlineCheckin || isOnlineCheckinComplete(onlineCheckin)) {
          redirectToPostCheckInApp();
          return false;
        }
      }
      const errors = error?.response?.data?.errors;
      if (errors && typeof errors === "object" && !Array.isArray(errors)) {
        setFieldErrors(Object.fromEntries(Object.entries(errors).map(([field, message]) => [field, flattenFirstMessage(message)])));
      }
      setGlobalError(getErrorMessage(error, t));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const goTo = async (nextStep, fields = []) => {
    const canContinue = fields.length ? await persistActiveCard(fields) : true;
    if (canContinue) {
      setStep(nextStep);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setGlobalError("");
    try {
      const preparedFile = await prepareUploadImage(file);
      setFiles((currentFiles) => ({
        ...currentFiles,
        [activeGuestId]: { ...currentFiles[activeGuestId], document_front_image: preparedFile },
      }));
      updateActiveCard("verification_method", "UPLOAD");
      updateActiveCard("verification_status", "VERIFIED");
      setStep("identity");
    } catch (error) {
      console.error("Failed to prepare upload image", error);
      setGlobalError(error.message ? t(error.message) : t("genericError"));
    } finally {
      event.target.value = "";
    }
  };

  const captureIdPhoto = async () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video?.videoHeight) {
      setGlobalError(t("camera.denied"));
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    try {
      const blob = await canvasToJpegBlob(canvas);
      const file = new File([blob], `id-${activeGuestId}.jpg`, { type: "image/jpeg" });
      setFiles((currentFiles) => ({
        ...currentFiles,
        [activeGuestId]: { ...currentFiles[activeGuestId], document_front_image: file },
      }));
      updateActiveCard("verification_method", "EXPRESS_SCAN");
      setStep("identity");
    } catch {
      setGlobalError(t("imageConvertError"));
    }
  };

  const applyReturningChoice = (choice) => {
    setReturningChoice(choice);
    if (choice === "no") {
      const applied = appliedPrefillRef.current;
      setCards((current) => current.map((card, index) => index === 0
        ? { ...card, ...Object.fromEntries(Object.entries(applied)
          .filter(([key, value]) => card[key] === value).map(([key]) => [key, ""])) }
        : card));
      appliedPrefillRef.current = {};
    }
    if (choice === "yes" && returningPrefill.available && cards.length) {
      const suggested = returningPrefill.fields || {};
      const allowed = [
        ...(!skipTravelPurpose ? ["travel_purpose"] : []),
        "nationality_country_code", "gender", "street_name", "address_line_2", "zip_code", "city", "country_code",
        ...(!skipCompanyInformation ? ["company_name", "company_vat_number"] : []),
      ];
      appliedPrefillRef.current = Object.fromEntries(allowed.filter((key) => !cards[0][key] && suggested[key]).map((key) => [key, suggested[key]]));
      setCards((current) => current.map((card, index) => index === 0
        ? { ...card, ...appliedPrefillRef.current }
        : card));
    }
  };

  const refreshServicesAndFolio = async () => {
    const paymentRequestId = beginPaymentRequest();
    const [offers, checkout] = await Promise.all([
      fetchCheckInServiceOffers(),
      fetchCheckInPaymentState(),
    ]);
    if (offers.selectedServicesRefreshError || offers.selected_services_refresh_error) {
      throw new Error(offers.selectedServicesRefreshError || offers.selected_services_refresh_error);
    }
    const normalized = normalizeIncludedSauna(
      offers.services || offers.serviceOffers || [],
      offers.selectedServices || offers.selected_services || []
    );
    const selected = normalized.selectedServices;
    const availableOffers = breakfastWizardOffers(normalized.offers, selected);
    setServiceOffers(availableOffers);
    setServiceIndex((index) => Math.min(index, Math.max(availableOffers.length - 1, 0)));
    setSelectedServices(selected);
    acceptPaymentResponse(checkout, paymentRequestId);
    setServicesReady(true);
    return { offers: availableOffers, selected, checkout };
  };

  const openServices = async () => {
    if (servicesReady) {
      setServiceIndex(0);
      setStep(serviceOffers.length ? "service" : "serviceSummary");
      return;
    }
    setSaving(true);
    setGlobalError("");
    try {
      const { offers } = await refreshServicesAndFolio();
      setServiceIndex(0);
      setStep(offers.length ? "service" : "serviceSummary");
    } catch (error) {
      setGlobalError(getErrorMessage(error, t));
    } finally {
      setSaving(false);
    }
  };

  const retryServices = async () => {
    setSaving(true);
    setGlobalError("");
    try {
      const { offers } = await refreshServicesAndFolio();
      if (!offers.length && step !== "serviceSummary") setStep("serviceSummary");
    } catch (error) {
      setGlobalError(getErrorMessage(error, t));
    } finally {
      setSaving(false);
    }
  };

  const finishGuest = async () => {
    const fields = [...REQUIRED_BY_STEP.details, ...(!skipTravelPurpose ? ["travel_purpose"] : []), ...(ID_VERIFICATION_ENABLED ? REQUIRED_BY_STEP.identity : []),
      ...REQUIRED_BY_STEP.address, ...REQUIRED_BY_STEP.review];
    if (activeCard.travel_purpose === "BUSINESS" && !skipCompanyInformation) fields.push("company_name");
    if (!validateFields(activeCard, fields)) {
      const missing = fields.filter((field) => isMissing(activeCard[field]));
      setStep(missing.some((field) => REQUIRED_BY_STEP.details.includes(field)) ? "details"
        : missing.includes("travel_purpose") ? (skipTravelPurpose ? "address" : "purpose")
        : ID_VERIFICATION_ENABLED && missing.some((field) => REQUIRED_BY_STEP.identity.includes(field)) ? "identity"
        : missing.some((field) => REQUIRED_BY_STEP.address.includes(field) || field === "company_name") ? "address"
        : "review");
      setGlobalError(t("checkMarkedFields"));
      return;
    }
    if (!await persistActiveCard()) return;
    setFieldErrors({});
    if (activeCardIndex + 1 < cards.length) {
      setActiveCardIndex(activeCardIndex + 1);
      setStep("details");
    } else {
      await openServices();
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const updateServiceDetails = (offer, field, value) => {
    setServiceErrorTarget(null);
    const key = serviceKey(offer);
    const details = { ...defaultServiceDetails(offer), ...serviceDetails[key], [field]: value };
    setServiceDetails((current) => ({ ...current, [key]: details }));
    setServiceBasket((current) => {
      if (!current[key]) return current;
      const next = { ...current, [key]: { offer, details } };
      if (breakfastCode(offer) === "FRST" && offer.childBreakfastOffer) {
        const childKey = serviceKey(offer.childBreakfastOffer);
        const childCount = Number(details.childBreakfastCount);
        if (Number.isInteger(childCount) && childCount > 0) {
          next[childKey] = { offer: offer.childBreakfastOffer,
            details: { ...defaultServiceDetails(offer.childBreakfastOffer), count: childCount, wholeStay: true } };
        } else delete next[childKey];
      }
      return next;
    });
  };

  const toggleServiceOffer = (offer) => {
    if (!servicesReady || saving || uncertainService) return;
    const key = serviceKey(offer);
    if (!key) return;
    setGlobalError("");
    if (serviceBasket[key]) {
      setServiceBasket((current) => {
        const next = { ...current };
        delete next[key];
        if (breakfastCode(offer) === "FRST" && offer.childBreakfastOffer) delete next[serviceKey(offer.childBreakfastOffer)];
        return next;
      });
      return;
    }
    const details = { ...defaultServiceDetails(offer), ...serviceDetails[key] };
    const error = breakfastCode(offer) === "FRST"
      ? breakfastSelectionError(offer, details, reservationGuestLimit(flowData?.reservation))
      : serviceSelectionError(offer, details);
    if (error) {
      setServiceErrorTarget({ field: serviceErrorField(error, offer, details) });
      setGlobalError(t(error));
      return;
    }
    if (isIncludedOffer(offer) || !canBookOffer(offer)) return;
    setServiceBasket((current) => {
      const next = { ...current, [key]: { offer, details } };
      if (breakfastCode(offer) === "FRST" && Number(details.childBreakfastCount) > 0) {
        const childOffer = offer.childBreakfastOffer;
        next[serviceKey(childOffer)] = { offer: childOffer,
          details: { ...defaultServiceDetails(childOffer), count: Number(details.childBreakfastCount), wholeStay: true } };
      }
      return next;
    });
  };

  const toggleServiceRemoval = (item) => {
    if (!canRemoveService(item) || saving || uncertainService) return;
    const key = serviceKey(item);
    setServiceRemovals((current) => {
      const next = { ...current };
      if (next[key]) delete next[key];
      else next[key] = item;
      return next;
    });
  };

  const confirmServiceSummary = async () => {
    if (!servicesReady || submittingServicesRef.current || uncertainService) return;
    for (const { offer, details } of Object.values(serviceBasket)) {
      const error = breakfastCode(offer) === "FRST"
        ? breakfastSelectionError(offer, details, reservationGuestLimit(flowData?.reservation))
        : serviceSelectionError(offer, details);
      if (error) {
        const index = serviceOffers.findIndex((item) => serviceKey(item) === serviceKey(offer) ||
          serviceKey(item.childBreakfastOffer) === serviceKey(offer));
        setServiceIndex(Math.max(index, 0));
        setStep("service");
        setServiceErrorTarget({ field: serviceErrorField(error, offer, details) });
        setGlobalError(t(error));
        return;
      }
    }
    submittingServicesRef.current = true;
    setSaving(true);
    setGlobalError("");
    let mutating = false;
    try {
      for (const [key, item] of Object.entries(serviceRemovals)) {
        mutating = true;
        const result = await removeCheckInService(serviceIdentity(item));
        if (result?.success === false) throw new Error(result.error || t("services.saveFailed"));
        mutating = false;
        setServiceRemovals((current) => { const next = { ...current }; delete next[key]; return next; });
        setSelectedServices((current) => current.filter((record) => !selectedForOffer(item, [record]).length));
      }
      for (const [key, { offer, details }] of Object.entries(serviceBasket)) {
        mutating = true;
        const result = await bookCheckInService(serviceBookPayload(offer, details));
        if (result?.success === false) throw new Error(result.error || t("services.saveFailed"));
        mutating = false;
        // Remove each acknowledged operation before refreshing, so a retry cannot book it twice.
        setServiceBasket((current) => { const next = { ...current }; delete next[key]; return next; });
        setSelectedServices((current) => [...current, result?.bookedService || {
          ...offer, ...serviceBookPayload(offer, details), canRemove: false,
        }]);
      }
      const { checkout } = await refreshServicesAndFolio();
      const payment = paymentFrom(checkout);
      if (paymentVerified(payment)) {
        await handleComplete();
      } else if (payment.required === true) {
        const outcome = await payBalance(checkout);
        if (outcome === "confirmed") await handleComplete();
      } else {
        setGlobalError(t("payment.pending"));
      }
    } catch (error) {
      const status = error?.response?.status;
      const uncertain = mutating && (!status || status >= 500 || status === 408);
      if (uncertain) setUncertainService(true);
      setGlobalError(uncertain ? t("services.uncertain") : getErrorMessage(error, t));
      setServicesReady(false);
      try { await refreshServicesAndFolio(); } catch { /* Require a fresh service and folio response before continuing. */ }
    } finally {
      submittingServicesRef.current = false;
      setSaving(false);
    }
  };

  const nextService = () => {
    setGlobalError("");
    setServiceErrorTarget(null);
    if (serviceIndex + 1 < serviceOffers.length) {
      setServiceIndex(serviceIndex + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setStep("serviceSummary");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const payBalance = async (knownCheckout = null) => {
    let paymentRequestId = beginPaymentRequest();
    setSaving(true);
    setGlobalError("");
    try {
      const checkout = knownCheckout || await fetchCheckInPaymentState();
      const currentPayment = paymentFrom(checkout);
      if (!acceptPaymentResponse(checkout, paymentRequestId)) return "stale";
      if (paymentVerified(currentPayment)) {
        return "confirmed";
      }
      const reusablePaymentUrl = currentPayment.payment_link_stale === true
        ? ""
        : currentPayment.payment_link_url;
      if (!reusablePaymentUrl && currentPayment.can_request_payment_link === false) {
        throw new Error(t("payment.linkUnavailable"));
      }
      const result = reusablePaymentUrl ? null : await createCheckInPaymentLink();
      if (!paymentVerification.isCurrentRequest(paymentRequestId)) return "stale";
      const url = reusablePaymentUrl || result?.payment_link_url || result?.checkout?.payment?.payment_link_url;
      if (result?.payment_required === false && !url) {
        paymentRequestId = beginPaymentRequest();
        const verifiedCheckout = await fetchCheckInPaymentState();
        const verifiedPayment = paymentFrom(verifiedCheckout);
        if (!acceptPaymentResponse(verifiedCheckout, paymentRequestId)) return "stale";
        if (paymentVerified(verifiedPayment)) {
          return "confirmed";
        }
      }
      if (!url) throw new Error(t("payment.linkUnavailable"));
      initialPaymentResumeRef.current = savePaymentResume(reservationCode, {
        flow: PAYMENT_FLOW_ARRIVAL,
        step: "payment",
        pending: true,
        settled: false,
        returnedFromProvider: false,
        startedAt: 0,
        attempts: 0,
      });
      redirectToPaymentLink(url);
      return "redirected";
    } catch (error) {
      if (paymentVerification.isCurrentRequest(paymentRequestId)) {
        setGlobalError(error?.response ? getErrorMessage(error, t) : error.message);
      }
      return "error";
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = useCallback(async () => {
    const paymentRequestId = beginPaymentRequest();
    setSaving(true);
    setGlobalError("");
    setBackendErrors({});
    try {
      const checkout = await fetchCheckInPaymentState();
      const payment = paymentFrom(checkout);
      if (!acceptPaymentResponse(checkout, paymentRequestId)) return false;
      if (!paymentVerified(payment)) {
        setGlobalError(t("payment.pending"));
        return false;
      }
      const result = await completeCheckIn();
      if (result?.success !== true || result?.dry_run !== false || result?.online_checkin?.is_complete !== true) {
        setGlobalError(t("completion.notConfirmed"));
        return false;
      }
      if (result?.room_access !== undefined) {
        storeRoomAccess(result.room_access);
        window.dispatchEvent(new Event("booking-update"));
      }
      redirectToPostCheckInApp();
      return true;
    } catch (error) {
      console.error("Failed to complete check-in", error);
      const data = error?.response?.data || {};
      const validation = data.validation_errors || data.errors || {};
      setBackendErrors(validation);
      const invalidGuestIndex = cards.findIndex((card) => Array.isArray(validation[String(card.guest_id)]));
      if (invalidGuestIndex >= 0) {
        const missing = validation[String(cards[invalidGuestIndex].guest_id)];
        setActiveCardIndex(invalidGuestIndex);
        setFieldErrors(Object.fromEntries(missing.map((field) => [field, t("required", { field: t(`fields.${field}`) })])));
        setStep(missing.some((field) => REQUIRED_BY_STEP.details.includes(field)) ? "details"
          : missing.includes("travel_purpose") ? (skipTravelPurpose ? "address" : "purpose")
          : ID_VERIFICATION_ENABLED && missing.some((field) => REQUIRED_BY_STEP.identity.includes(field)) ? "identity"
          : missing.some((field) => REQUIRED_BY_STEP.address.includes(field) || field === "company_name") ? "address"
          : "review");
      }
      if (error?.response?.status === 402) setStep("payment");
      setGlobalError(getErrorMessage(error, t));
      return false;
    } finally {
      setSaving(false);
    }
  }, [acceptPaymentResponse, beginPaymentRequest, cards, redirectToPostCheckInApp, skipTravelPurpose, t]);

  useEffect(() => {
    if (
      !autoCompletePaymentReturnRef.current ||
      autoCompletionStartedRef.current ||
      loading ||
      !flowData ||
      !paymentConfirmed
    ) {
      return undefined;
    }

    let isMounted = true;
    autoCompletionStartedRef.current = true;
    setAutoCompletingPaymentReturn(true);
    handleComplete().then((completed) => {
      if (isMounted && !completed) setAutoCompletingPaymentReturn(false);
    });

    return () => {
      isMounted = false;
    };
  }, [flowData, handleComplete, loading, paymentConfirmed]);

  useEffect(() => {
    if (
      autoCompletePaymentReturnRef.current &&
      !paymentConfirmed &&
      ["timed_out", "error"].includes(paymentVerification.status)
    ) {
      setAutoCompletingPaymentReturn(false);
    }
  }, [paymentConfirmed, paymentVerification.status]);

  const checkPaymentAgain = useCallback(() => {
    if (!autoCompletePaymentReturnRef.current || paymentConfirmed) return;
    setGlobalError("");
    setAutoCompletingPaymentReturn(true);
    setPaymentOpened(true);
    restartPaymentVerification();
  }, [paymentConfirmed, restartPaymentVerification]);

  useEffect(() => {
    if (
      !autoCompletePaymentReturnRef.current ||
      paymentConfirmed ||
      paymentVerification.status !== "timed_out"
    ) {
      return undefined;
    }

    const verifyOnFocus = () => {
      if (document.visibilityState !== "hidden") checkPaymentAgain();
    };
    window.addEventListener("focus", verifyOnFocus);
    window.addEventListener("pageshow", verifyOnFocus);
    return () => {
      window.removeEventListener("focus", verifyOnFocus);
      window.removeEventListener("pageshow", verifyOnFocus);
    };
  }, [checkPaymentAgain, paymentConfirmed, paymentVerification.status]);

  useEffect(() => {
    if (!globalError || !flowData || (step !== "service" && step !== "serviceSummary")) return undefined;
    const timeout = window.setTimeout(() => setGlobalError(""), 5000);
    return () => window.clearTimeout(timeout);
  }, [globalError, flowData, step]);

  useEffect(() => {
    if (!Object.values(fieldErrors).some(Boolean)) return;
    const field = document.querySelector(".checkin-section .form-field.has-error, .checkin-section .consent-row.has-error, .checkin-section .travel-purpose.has-error");
    if (!field) return;
    field.scrollIntoView?.({ behavior: "smooth", block: "center" });
    field.querySelector("input, select, button")?.focus({ preventScroll: true });
  }, [fieldErrors, step, activeCardIndex]);

  useEffect(() => {
    if (step !== "service" || !serviceErrorTarget) return;
    const field = document.querySelector(`.checkin-service-screen [data-error-target="${serviceErrorTarget.field}"]`) ||
      document.querySelector(".checkin-service-screen .checkin-service-options");
    if (!field) return;
    field.scrollIntoView?.({ behavior: "smooth", block: "center" });
    field.querySelector("input:not(:disabled), select:not(:disabled)")?.focus({ preventScroll: true });
  }, [serviceErrorTarget, step, serviceIndex]);

  const progressSteps = STEP_KEYS.flatMap((key) => key === "service"
    ? serviceOffers.map((_, index) => `service-${index}`) : [key]);
  const progressStep = step === "service" ? `service-${serviceIndex}` : step === "payment" ? "serviceSummary" : step;
  const stepIndex = Math.max(progressSteps.indexOf(progressStep), 0);
  const progress = Math.round(((stepIndex + 1) / progressSteps.length) * 100);
  const reservation = flowData?.reservation || {};
  const roomNames = reservation.rooms?.map((room) => room.name || room.room_number).filter(Boolean).join(", ");
  const currentOffer = serviceOffers[serviceIndex];
  const currentServiceName = currentOffer ? serviceDisplayName(currentOffer, t) : "";
  const currentServiceDescription = currentOffer ? serviceDisplayDescription(currentOffer) : "";
  const currentOfferKey = currentOffer ? serviceKey(currentOffer) : "";
  const currentBreakfast = currentOffer && isBreakfastService(currentOffer);
  const currentSelected = currentOffer ? currentBreakfast
    ? selectedServices.filter(isBreakfastService) : selectedForOffer(currentOffer, selectedServices) : [];
  const currentPendingRemoval = currentSelected.length > 0 &&
    currentSelected.every((item) => serviceRemovals[serviceKey(item)]);
  const currentIncluded = currentOffer && (isIncludedOffer(currentOffer) || currentSelected.some(isIncludedOffer));
  const currentInBasket = Boolean(serviceBasket[currentOfferKey]);
  const currentBooked = currentOffer && (currentSelected.length > 0 || Number(currentOffer.bookedCount ?? currentOffer.booked_count ?? currentOffer.existingCount ?? 0) > 0);
  const currentPrice = currentOffer ? moneyLabel(currentOffer.displayUnitPrice || currentOffer.display_unit_price, language) : "";
  const currentCanBook = currentOffer && currentOfferKey && canBookOffer(currentOffer) && serviceQuantityLimit(currentOffer) !== 0 && !currentIncluded &&
    (!currentBreakfast || !currentBooked) && Boolean(currentPrice);
  const currentDetails = currentOffer ? { ...defaultServiceDetails(currentOffer), childBreakfastCount: 0,
    freeKidBreakfastCount: 0, ...serviceDetails[currentOfferKey] } : {};
  const currentGuestLimit = reservationGuestLimit(reservation);
  const currentChildOffer = breakfastCode(currentOffer) === "FRST" ? currentOffer.childBreakfastOffer : null;
  const currentChildCanBook = currentChildOffer && canBookOffer(currentChildOffer) && serviceQuantityLimit(currentChildOffer) !== 0 &&
    Boolean(moneyLabel(currentChildOffer.displayUnitPrice || currentChildOffer.display_unit_price, language));
  const adultCount = Number(currentDetails.count) || 0;
  const childCount = Number(currentDetails.childBreakfastCount) || 0;
  const freeCount = Number(currentDetails.freeKidBreakfastCount) || 0;
  const breakfastMax = (offerLimit, otherCount) => {
    const limits = [offerLimit, currentGuestLimit === undefined ? undefined : Math.max(0, currentGuestLimit - otherCount)]
      .filter((value) => value !== undefined);
    return limits.length ? Math.min(...limits) : undefined;
  };
  const adultBreakfastMax = currentOffer ? breakfastMax(serviceQuantityLimit(currentOffer), childCount + freeCount) : undefined;
  const childBreakfastMax = currentChildCanBook ? breakfastMax(serviceQuantityLimit(currentChildOffer), adultCount + freeCount) : 0;
  const freeBreakfastMax = breakfastMax(undefined, adultCount + childCount);
  const breakfastTotal = breakfastCode(currentOffer) === "FRST" ? breakfastSelectionPrice(currentOffer, currentDetails) : null;
  const basketSummary = serviceBasketSummary(serviceBasket, serviceRemovals, paymentData);
  const basketBreakfastAdult = Object.values(serviceBasket).find(({ offer }) => breakfastCode(offer) === "FRST");
  const includedSelected = selectedServices.filter((item) => isBreakfastService(item) && isIncludedOffer(item));
  const summaryIncluded = serviceOffers.filter((offer) => isIncludedOffer(offer) &&
    !includedSelected.some((item) => selectedForOffer(offer, [item]).length)).concat(includedSelected);
  const summaryBooked = selectedServices.filter((item) => !isIncludedOffer(item) && !summaryIncluded.some((offer) => selectedForOffer(offer, [item]).length));
  const hasServiceSummary = serviceOffers.length > 0 || selectedServices.length > 0 || Object.keys(serviceBasket).length > 0;
  const verifyingReturnedPayment = autoCompletePaymentReturnRef.current && !paymentConfirmed;

  if (loading || autoCompletingPaymentReturn || (step === "serviceSummary" && saving)) {
    return (
      <main className="checkin-app">
        <div className="checkin-loader">
          <LoadingSpinner />
          <p>{step === "serviceSummary" && saving ? t("services.processing") : t("loading")}</p>
        </div>
      </main>
    );
  }

  if (globalError && !flowData) {
    return (
      <main className="checkin-app">
        <Header language={language} setLanguage={handleLanguageChange} t={t} />
        <section className="checkin-section">
          <h1>{t("checkin")}</h1>
          <p className="checkin-error-block">{globalError}</p>
          <button className="checkin-button secondary" type="button" onClick={() => navigate(-1)}>
            {t("back")}
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className={`checkin-app ${step === "idCapture" ? "camera-active" : ""}`}>
      {step !== "idCapture" && <Header language={language} setLanguage={handleLanguageChange} t={t} />}
      {step !== "complete" && step !== "idCapture" && (
        <div className="checkin-progress" aria-label={t("progress")}>
          <span style={{ width: `${progress}%` }} />
        </div>
      )}

      {globalError && (
        <div className="checkin-error-banner" role="alert">
          <span>{globalError}</span>
          <button type="button" aria-label={t("dismissError")} onClick={() => setGlobalError("")}>×</button>
        </div>
      )}

      {step === "overview" && (
        <section className="checkin-section checkin-overview">
          <p className="checkin-kicker">{t("overview.kicker")}</p>
          <h1>{t("overview.title")}</h1>
          <div className="reservation-lines">
            <div>
              <span>{t("overview.reservation")}</span>
              <strong>{reservationCode}</strong>
            </div>
            <div>
              <span>{t("overview.guest")}</span>
              <strong>{activeCard.guest_name || t("guestFallback")}</strong>
            </div>
            {roomNames && (
              <div>
                <span>{t("overview.room")}</span>
                <strong>{roomNames}</strong>
              </div>
            )}
            {(reservation.arrival || reservation.departure) && (
              <div>
                <span>{t("overview.stay")}</span>
                <strong>
                  {reservation.arrival || localStorage.getItem("arrival")} -{" "}
                  {reservation.departure || localStorage.getItem("departure")}
                </strong>
              </div>
            )}
          </div>
          {returningPrefill.available === true && cards.length > 0 && (
            <div className="checkin-returning">
              <p>{t("overview.returningQuestion", { name: returningPrefill.displayName || returningPrefill.display_name })}</p>
              <div className="segment-group two-columns">
                <button type="button" className={returningChoice === "yes" ? "active" : ""} onClick={() => applyReturningChoice("yes")}>{t("overview.yes")}</button>
                <button type="button" className={returningChoice === "no" ? "active" : ""} onClick={() => applyReturningChoice("no")}>{t("overview.no")}</button>
              </div>
            </div>
          )}
          <div className="bottom-actions">
            <button className="checkin-button primary" type="button" disabled={!cards.length || (returningPrefill.available === true && !returningChoice)} onClick={() => setStep("details")}>
              {t("startCheckin")}
            </button>
          </div>
        </section>
      )}

      {step === "details" && (
        <section className="checkin-section">
          <p className="checkin-kicker">{t("overview.guest")} {activeCardIndex + 1} / {cards.length}</p>
          <h1>{t("details.title")}</h1>
          <p className="guest-name-line">{activeCard.guest_name}</p>
          <FormInput label={t("fields.email")} type="email" value={activeCard.email} error={fieldErrors.email} onChange={(value) => updateActiveCard("email", value)} />
          <FormInput label={t("fields.phone")} type="tel" value={activeCard.phone} error={fieldErrors.phone} onChange={(value) => updateActiveCard("phone", value)} />
          <FormSelect label={t("fields.nationality_country_code")} value={activeCard.nationality_country_code} options={countries} error={fieldErrors.nationality_country_code} onChange={(value) => updateActiveCard("nationality_country_code", value)} placeholder={t("selectPlaceholder")} />
          <FormSelect label={t("fields.gender")} value={activeCard.gender} options={genders} error={fieldErrors.gender} onChange={(value) => updateActiveCard("gender", value)} placeholder={t("selectPlaceholder")} />
          <div className="bottom-actions">
            <button className="checkin-button primary" type="button" disabled={saving} onClick={() => goTo(ID_VERIFICATION_ENABLED ? "idIntro" : nextStepAfterIdentity, REQUIRED_BY_STEP.details)}>{saving ? t("saving") : t("continue")}</button>
            {activeCardIndex > 0 && <button className="checkin-link-button" type="button" onClick={() => { setActiveCardIndex(activeCardIndex - 1); setStep("review"); }}>{t("back")}</button>}
          </div>
        </section>
      )}

      {ID_VERIFICATION_ENABLED && step === "idIntro" && (
        <section className="checkin-section">
          <h1>{t("idIntro.title")}</h1>
          <p className="checkin-copy">
            {t("idIntro.copyIntro")}
            <strong>
              {t("documents.idCard")}, {t("documents.passport")}, {t("documents.driverLicense")}
            </strong>
            {t("idIntro.copyOutro")}
          </p>
          <div className="bottom-actions">
            <button className="checkin-button primary" type="button" onClick={() => setStep("idCapture")}>
              {t("idIntro.expressScan")}
            </button>
            <button
              className="checkin-button secondary"
              type="button"
              onClick={() => fileInputRef.current?.click()}
            >
              {t("uploadId")}
            </button>
            <button className="checkin-link-button" type="button" onClick={() => setStep("details")}>
              {t("back")}
            </button>
          </div>
          <input
            ref={fileInputRef}
            className="visually-hidden"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            onChange={handleUpload}
          />
        </section>
      )}

      {ID_VERIFICATION_ENABLED && step === "idCapture" && (
        <section className="camera-screen">
          <video ref={videoRef} autoPlay playsInline muted className="camera-preview" />
          <div className="camera-overlay">
            <div className="camera-topbar">
              <button type="button" onClick={() => setStep("idIntro")}>{t("back")}</button>
              <button type="button" onClick={() => setCameraState("help")}>{t("camera.help")}</button>
            </div>
            <div className="document-frame" />
            <p className="camera-instruction">
              {cameraState === "ready" && t("camera.ready")}
              {cameraState === "requesting" && t("camera.requesting")}
              {cameraState === "denied" && t("camera.denied")}
              {cameraState === "unavailable" && t("camera.unavailable")}
              {cameraState === "help" && t("camera.helpText")}
            </p>
            <div className="camera-actions">
              <button
                className="checkin-button primary"
                type="button"
                onClick={cameraState === "ready" ? captureIdPhoto : () => setStep("identity")}
            >
                {t("confirmData")}
              </button>
              <button
                className="checkin-button secondary"
                type="button"
                onClick={() => fileInputRef.current?.click()}
            >
                {t("uploadId")}
              </button>
            </div>
          </div>
          <input
            ref={fileInputRef}
            className="visually-hidden"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            onChange={handleUpload}
          />
        </section>
      )}

      {ID_VERIFICATION_ENABLED && step === "identity" && (
        <section className="checkin-section">
          <h1>{t("identity.title")}</h1>
          <p className="guest-name-line">{activeCard.guest_name}</p>
          <FormSelect
            label={t("fields.nationality_country_code")}
            value={activeCard.nationality_country_code}
            options={countries}
            error={fieldErrors.nationality_country_code}
            onChange={(value) => updateActiveCard("nationality_country_code", value)}
            placeholder={t("selectPlaceholder")}
          />
          <FormSelect
            label={t("fields.document_type")}
            value={activeCard.document_type}
            options={documentTypes}
            error={fieldErrors.document_type}
            onChange={(value) => updateActiveCard("document_type", value)}
            placeholder={t("selectPlaceholder")}
          />
          <FormInput
            label={t("fields.document_number")}
            value={activeCard.document_number}
            error={fieldErrors.document_number}
            onChange={(value) => updateActiveCard("document_number", value)}
          />
          <FormInput
            label={t("fields.document_issued_on")}
            type="date"
            value={activeCard.document_issued_on}
            error={fieldErrors.document_issued_on}
            onChange={(value) => updateActiveCard("document_issued_on", value)}
          />
          <FormInput
            label={t("fields.date_of_birth")}
            type="date"
            value={activeCard.date_of_birth}
            error={fieldErrors.date_of_birth}
            onChange={(value) => updateActiveCard("date_of_birth", value)}
          />
          <FormSelect
            label={t("fields.gender")}
            value={activeCard.gender}
            options={genders}
            error={fieldErrors.gender}
            onChange={(value) => updateActiveCard("gender", value)}
            placeholder={t("selectPlaceholder")}
          />
          <div className="bottom-actions">
            <button
              className="checkin-button primary"
              type="button"
              disabled={saving}
              onClick={() => goTo(nextStepAfterIdentity, REQUIRED_BY_STEP.identity)}
            >
              {saving ? t("saving") : t("continue")}
            </button>
            <button className="checkin-link-button" type="button" onClick={() => setStep("idIntro")}>{t("back")}</button>
          </div>
        </section>
      )}

      {step === "purpose" && !skipTravelPurpose && (
        <section className="checkin-section">
          <h1>{t("purpose.title")}</h1>
          <fieldset className={`travel-purpose ${fieldErrors.travel_purpose ? "has-error" : ""}`}>
            <legend>{t("purpose.question")}</legend>
            <div className="segment-group two-columns">
              {["LEISURE", "BUSINESS"].map((purpose) => (
                <button key={purpose} className={activeCard.travel_purpose === purpose ? "active" : ""} type="button" onClick={() => updateActiveCard("travel_purpose", purpose)}>{t(purpose === "LEISURE" ? "purpose.private" : "purpose.business")}</button>
              ))}
            </div>
            {fieldErrors.travel_purpose && <small className="checkin-field-error">{fieldErrors.travel_purpose}</small>}
          </fieldset>
          <div className="bottom-actions">
            <button className="checkin-button primary" type="button" disabled={saving} onClick={() => goTo("address", ["travel_purpose"])}>{saving ? t("saving") : t("continue")}</button>
            <button className="checkin-link-button" type="button" onClick={() => setStep(ID_VERIFICATION_ENABLED ? "identity" : "details")}>{t("back")}</button>
          </div>
        </section>
      )}

      {step === "address" && (
        <section className="checkin-section">
          <h1>{t("address.title")}</h1>
          {activeCard.travel_purpose === "BUSINESS" && !skipCompanyInformation && (
            <>
              <FormInput label={t("fields.company_name")} value={activeCard.company_name} error={fieldErrors.company_name} onChange={(value) => updateActiveCard("company_name", value)} />
              <FormInput label={t("fields.company_vat_number")} value={activeCard.company_vat_number} onChange={(value) => updateActiveCard("company_vat_number", value)} />
            </>
          )}
          <FormInput
            label={t("fields.street_name")}
            value={activeCard.street_name}
            error={fieldErrors.street_name}
            onChange={(value) => updateActiveCard("street_name", value)}
          />
          <FormInput
            label={t("fields.address_line_2")}
            value={activeCard.address_line_2}
            onChange={(value) => updateActiveCard("address_line_2", value)}
          />
          <FormInput
            label={t("fields.zip_code")}
            value={activeCard.zip_code}
            error={fieldErrors.zip_code}
            onChange={(value) => updateActiveCard("zip_code", value)}
          />
          <FormInput
            label={t("fields.city")}
            value={activeCard.city}
            error={fieldErrors.city}
            onChange={(value) => updateActiveCard("city", value)}
          />
          <FormSelect
            label={t("fields.country_code")}
            value={activeCard.country_code}
            options={countries}
            error={fieldErrors.country_code}
            onChange={(value) => updateActiveCard("country_code", value)}
            placeholder={t("selectPlaceholder")}
          />
          <div className="bottom-actions">
            <button
              className="checkin-button primary"
              type="button"
              disabled={saving}
              onClick={() => goTo("review", [...REQUIRED_BY_STEP.address, ...(activeCard.travel_purpose === "BUSINESS" && !skipCompanyInformation ? ["company_name"] : [])])}
            >
              {saving ? t("saving") : t("continue")}
            </button>
            <button className="checkin-link-button" type="button" onClick={() => setStep(previousStepBeforeAddress)}>{t("back")}</button>
          </div>
        </section>
      )}

      {step === "review" && (
        <section className="checkin-section">
          <h1>{t("review.title")}</h1>
          <p className="checkin-kicker">{t("overview.guest")} {activeCardIndex + 1} / {cards.length}</p>
          <div className="review-list">
            <ReviewLine label={t("overview.guest")} value={activeCard.guest_name} />
            {ID_VERIFICATION_ENABLED && <ReviewLine label={t("review.document")} value={`${activeCard.document_type || "-"} ${activeCard.document_number || ""}`} />}
            <ReviewLine label={t("review.address")} value={`${activeCard.street_name || "-"}, ${activeCard.zip_code || ""} ${activeCard.city || ""}`} />
            <ReviewLine label={t("review.travelPurpose")} value={travelPurposes.find((item) => item.value === activeCard.travel_purpose)?.label} />
          </div>
          <ConsentRow
            label={t("review.terms")}
            checked={activeCard.terms_accepted}
            error={fieldErrors.terms_accepted}
            onChange={(checked) => updateActiveCard("terms_accepted", checked)}
          />
          <ConsentRow
            label={t("review.privacy")}
            checked={activeCard.privacy_accepted}
            error={fieldErrors.privacy_accepted}
            onChange={(checked) => updateActiveCard("privacy_accepted", checked)}
          />
          {/* <ConsentRow
            label={t("review.contactPermission")}
            checked={activeCard.contact_permission}
            onChange={(checked) => updateActiveCard("contact_permission", checked)}
          /> */}
          {Object.keys(backendErrors || {}).length > 0 && (
            <div className="checkin-error-block">
              {t("review.backendError")}
            </div>
          )}
          <div className="bottom-actions">
            <button
              className="checkin-button primary"
              type="button"
              disabled={saving}
              onClick={finishGuest}
            >
              {saving ? t("saving") : activeCardIndex + 1 < cards.length ? t("review.nextGuest") : t("review.continueServices")}
            </button>
            <button className="checkin-link-button" type="button" onClick={() => setStep("address")}>{t("back")}</button>
          </div>
        </section>
      )}

      {step === "service" && currentOffer && (
        <section className="checkin-service-screen">
          <div className="checkin-service-photo" role="img" aria-label={currentServiceName} style={{ backgroundImage: `url("${serviceImage(currentOffer)}")` }} />
          <div className="checkin-service-content">
            <div className="checkin-service-inner">
              <p className="checkin-kicker">{t("services.kicker")} {serviceIndex + 1} / {serviceOffers.length}</p>
              <h1>{currentServiceName}</h1>
              {currentServiceDescription && <p className="checkin-copy">{currentServiceDescription}</p>}
              <div className="checkin-service-price">
                <strong className={currentIncluded && !currentPendingRemoval ? "checkin-service-included" : undefined}>{currentIncluded ? currentPrice || t("services.included") : currentPrice || t("services.priceUnavailable")}</strong>
                {currentIncluded && (currentPendingRemoval
                  ? <span>{t("services.pendingRemoval")}</span> : <ServiceStatus label={t("services.includedState")} />)}
                {(currentBooked || currentInBasket) && !currentIncluded && (currentPendingRemoval && !currentInBasket
                  ? <span>{t("services.pendingRemoval")}</span>
                  : <ServiceStatus label={t(currentInBasket ? "services.inBasket" : "services.selectedState")} />)}
              </div>
              {isIncludedSaunaOffer(currentOffer) && (
                <aside className="checkin-sauna-notice" role="note">
                  <strong>{t("services.saunaNoticeLead")}</strong>
                  <p>{t("services.saunaNotice")}</p>
                </aside>
              )}
              {!currentCanBook && !currentIncluded && !currentBooked && currentOffer.unavailableReason && <p className="checkin-copy">{currentOffer.unavailableReason}</p>}
              {currentSelected.map((item, index) => canRemoveService(item) && (
                <button key={`${serviceKey(item)}-${index}`} type="button" className="checkin-service-action" disabled={saving || !servicesReady || uncertainService} onClick={() => toggleServiceRemoval(item)}>{t(serviceRemovals[serviceKey(item)] ? "services.keep" : "services.remove")}</button>
              ))}
              {currentCanBook && <fieldset className="checkin-service-options" disabled={saving || uncertainService}>
                {breakfastCode(currentOffer) === "FRST" ? <>
                  <div className="checkin-breakfast-counts">
                    <label className="form-field" data-error-target="count">
                      <span>{t("services.breakfastAdults")}</span>
                      <small>{currentPrice || t("services.priceUnavailable")}</small>
                      <input type="number" min="1" step="1" max={adultBreakfastMax} value={currentDetails.count}
                        onChange={(event) => updateServiceDetails(currentOffer, "count", event.target.value)} />
                    </label>
                    <label className="form-field" data-error-target="childBreakfastCount">
                      <span>{t("services.breakfastChildren")}</span>
                      <small>{currentChildOffer ? moneyLabel(currentChildOffer.displayUnitPrice || currentChildOffer.display_unit_price, language) || t("services.priceUnavailable") : t("services.notAvailable")}</small>
                      <input type="number" min="0" step="1" max={childBreakfastMax} value={currentDetails.childBreakfastCount}
                        disabled={!currentChildCanBook || childBreakfastMax === 0}
                        onChange={(event) => updateServiceDetails(currentOffer, "childBreakfastCount", event.target.value)} />
                    </label>
                    <label className="form-field" data-error-target="freeKidBreakfastCount">
                      <span>{t("services.breakfastFreeChildren")}</span>
                      <small>{t("services.free")}</small>
                      <input type="number" min="0" step="1" max={freeBreakfastMax} value={currentDetails.freeKidBreakfastCount}
                        disabled={freeBreakfastMax === 0}
                        onChange={(event) => updateServiceDetails(currentOffer, "freeKidBreakfastCount", event.target.value)} />
                    </label>
                  </div>
                  {currentChildOffer && !currentChildCanBook && currentChildOffer.unavailableReason && <p className="checkin-copy">{currentChildOffer.unavailableReason}</p>}
                  <ReviewLine label={t("services.breakfastTotal")} value={moneyLabel(breakfastTotal, language) || t("services.priceUnavailable")} />
                </> : serviceQuantityLimit(currentOffer) !== 1 && (
                  <label className="form-field" data-error-target="count">
                    <span>{t("services.quantity")}</span>
                    <input type="number" min="1" step="1" max={serviceQuantityLimit(currentOffer)} value={currentDetails.count} onChange={(event) => updateServiceDetails(currentOffer, "count", event.target.value)} />
                  </label>
                )}
                {!isTaxiOffer(currentOffer) && !isLateCheckoutOffer(currentOffer) && !isParkingOffer(currentOffer) && serviceDates(currentOffer).length > 0 && (
                  requiresWholeStay(currentOffer) && !isSaunaOffer(currentOffer) ? <p className="checkin-copy">{t("services.wholeStay")}</p> : <>
                    {!isSaunaOffer(currentOffer) && <ConsentRow label={t("services.wholeStay")} checked={currentDetails.wholeStay} onChange={(value) => updateServiceDetails(currentOffer, "wholeStay", value)} />}
                    {(!currentDetails.wholeStay || isSaunaOffer(currentOffer)) && <fieldset className="checkin-service-dates" data-error-target="dates">
                      <legend>{t("services.dates")}</legend>
                      {serviceDates(currentOffer).map((date) => <ConsentRow key={date} label={date} checked={currentDetails.dates.includes(date)} onChange={(checked) => updateServiceDetails(currentOffer, "dates", checked ? [...currentDetails.dates, date] : currentDetails.dates.filter((item) => item !== date))} />)}
                    </fieldset>}
                  </>
                )}
              {currentCanBook && isParkingOffer(currentOffer) && (
                <FormInput label={t("services.licensePlate")} value={currentDetails.licensePlate} errorTarget="licensePlate" onChange={(value) => updateServiceDetails(currentOffer, "licensePlate", value)} />
              )}
              {currentCanBook && isTaxiOffer(currentOffer) && (
                <>
                  <FormSelect label={t("services.pickupDate")} value={currentDetails.taxiPickupDate} options={(currentOffer.taxiPickupDates || currentOffer.taxi_pickup_dates || []).map((date) => ({ value: date, label: date }))} placeholder={t("selectPlaceholder")} errorTarget="taxiPickupDate" onChange={(value) => updateServiceDetails(currentOffer, "taxiPickupDate", value)} />
                  <QuarterHourTimeInput label={t("services.pickupTime")} hourLabel={t("services.pickupHour")} minuteLabel={t("services.pickupMinute")} value={currentDetails.taxiPickupTime} errorTarget="taxiPickupTime" onChange={(value) => updateServiceDetails(currentOffer, "taxiPickupTime", value)} />
                  <FormInput label={t("services.pickupNote")} value={currentDetails.taxiPickupNote} onChange={(value) => updateServiceDetails(currentOffer, "taxiPickupNote", value)} />
                </>
              )}
              </fieldset>}
              {basketSummary.entries.length > 0 && <ReviewLine label={t("services.basketTotal")} value={moneyLabel(basketSummary.extras, language) || t("services.priceUnavailable")} />}
            </div>
          </div>
          <div className="bottom-actions">
            {currentCanBook && <button className="checkin-button primary" type="button" disabled={saving || !servicesReady || uncertainService} onClick={() => toggleServiceOffer(currentOffer)}>{t(currentInBasket ? "services.remove" : "services.add")}</button>}
            <button className={`checkin-button ${currentCanBook ? "secondary" : "primary"}`} type="button" disabled={saving || !servicesReady} onClick={nextService}>{currentIncluded || currentBooked || currentInBasket ? t("continue") : t("services.skipOne")}</button>
            {!servicesReady && <button className="checkin-button secondary" type="button" disabled={saving} onClick={retryServices}>{t("services.retry")}</button>}
            <button className="checkin-link-button" type="button" onClick={() => { if (serviceIndex > 0) setServiceIndex(serviceIndex - 1); else setStep("review"); }}>{t("back")}</button>
          </div>
        </section>
      )}

      {step === "serviceSummary" && (
        <section className="checkin-section checkin-services-summary">
          <p className="checkin-kicker">{t("services.kicker")}</p>
          <h1>{t("services.summary")}</h1>
          {summaryIncluded.map((offer) => <div className="checkin-basket-item" key={serviceKey(offer)}>
            <ReviewLine label={serviceDisplayName(offer, t)} value={serviceRemovals[serviceKey(offer)]
              ? t("services.pendingRemoval") : <><span>{moneyLabel(offer.displayUnitPrice || offer.display_unit_price, language)}</span><ServiceStatus label={t("services.included")} /></>} />
            {canRemoveService(offer) && <button type="button" className="checkin-service-action" disabled={saving || uncertainService}
              onClick={() => toggleServiceRemoval(offer)}>{t(serviceRemovals[serviceKey(offer)] ? "services.keep" : "services.remove")}</button>}
          </div>)}
          {summaryBooked.map((item, index) => <div className="checkin-basket-item" key={`${serviceKey(item)}-${index}`}>
            <ReviewLine label={serviceDisplayName(item, t)} value={serviceRemovals[serviceKey(item)]
              ? t("services.pendingRemoval") : <ServiceStatus label={t("services.booked")} />} />
            {canRemoveService(item) && <button type="button" className="checkin-service-action" disabled={saving || uncertainService} onClick={() => toggleServiceRemoval(item)}>{t(serviceRemovals[serviceKey(item)] ? "services.keep" : "services.remove")}</button>}
          </div>)}
          {basketSummary.entries.map(({ offer, details, price }) => <div className="checkin-basket-item" key={serviceKey(offer)}>
            <ReviewLine label={serviceDisplayName(offer, t)} value={moneyLabel(price, language) || t("services.priceUnavailable")} />
            <ServiceStatus label={t("services.selectedState")} />
            <p>{breakfastCode(offer) === "FRST" ? t("services.breakfastAdults") : breakfastCode(offer) === "FRSTKI" ? t("services.breakfastChildren") : t("services.quantity")}: {details.count}</p>
            {breakfastCode(offer) === "FRST" && Number(details.freeKidBreakfastCount) > 0 && <p>{t("services.breakfastFreeChildren")}: {details.freeKidBreakfastCount}</p>}
            {!isTaxiOffer(offer) && !isLateCheckoutOffer(offer) && !isParkingOffer(offer) && (details.dates.length > 0 || (details.wholeStay && !isSaunaOffer(offer))) &&
              <p>{details.wholeStay && !isSaunaOffer(offer) ? t("services.wholeStay") : details.dates.join(", ")}</p>}
            {isParkingOffer(offer) && <p>{t("services.licensePlate")}: {details.licensePlate}</p>}
            {isTaxiOffer(offer) && <p>{details.taxiPickupDate} {details.taxiPickupTime} {details.taxiPickupNote}</p>}
            <button type="button" className="checkin-service-action" disabled={saving || uncertainService}
              onClick={() => {
                if (breakfastCode(offer) === "FRSTKI" && basketBreakfastAdult) updateServiceDetails(basketBreakfastAdult.offer, "childBreakfastCount", 0);
                else toggleServiceOffer(offer);
              }}>{t("services.remove")}</button>
          </div>)}
          {!basketSummary.entries.length && !summaryBooked.length && !summaryIncluded.length && <p className="checkin-copy">{t("services.noneSelected")}</p>}
          {basketSummary.entries.length > 0 && <ReviewLine label={t("services.basketTotal")} value={moneyLabel(basketSummary.extras, language) || t("services.priceUnavailable")} />}
          <ReviewLine label={t("services.estimatedTotal")} value={moneyLabel(basketSummary.total, language) || t("services.priceUnavailable")} />
          {uncertainService && <p className="checkin-error-block">{t("services.uncertain")}</p>}
          <div className="bottom-actions">
            <button type="button" className="checkin-button primary" disabled={saving || !servicesReady || uncertainService} onClick={confirmServiceSummary}>
              {!paymentBypassActive && (paymentData?.required === true || (basketSummary.entries.length > 0 && Number(basketSummary.total?.amount) > 0)) ? t("payment.pay") : t("continue")}
            </button>
            {!servicesReady && <button type="button" className="checkin-button secondary" disabled={saving} onClick={retryServices}>{t("services.retry")}</button>}
            <button type="button" className="checkin-link-button" disabled={saving} onClick={() => { if (serviceOffers.length) { setServiceIndex(serviceOffers.length - 1); setStep("service"); } else setStep("review"); }}>{t("back")}</button>
          </div>
        </section>
      )}

      {step === "payment" && (
        <section className="checkin-section checkin-payment-section">
          <p className="checkin-kicker">{t("payment.kicker")}</p>
          <h1>{t("payment.title")}</h1>
          <div className="review-list">
            <ReviewLine label={t("overview.reservation")} value={reservationCode} />
            <ReviewLine label={t("payment.balance")} value={paymentVerified(paymentData || {}) ? t("payment.noneDue") : moneyLabel(paymentData, language) || t("payment.unavailable")} />
            {(checkoutData?.selected_folio?.charges || checkoutData?.folios?.[0]?.charges || []).map((charge, index) => (
              <ReviewLine key={charge.id || index} label={charge.name || charge.description || t("payment.charge")} value={moneyLabel(chargeMoney(charge), language)} />
            ))}
          </div>
          {paymentData?.required === true && !verifyingReturnedPayment && <p className="checkin-copy">{t("payment.due")}</p>}
          {!verifyingReturnedPayment && !paymentConfirmed && paymentData?.payment_link_stale === true && <p className="checkin-error-block">{t("payment.linkExpired")}</p>}
          {paymentOpened && paymentData?.required === true && <p className="checkin-copy">{t("payment.returned")}</p>}
          {!paymentConfirmed && paymentVerification.status === "verifying" && <p className="checkin-copy" role="status">{t("payment.verifying")}</p>}
          {!paymentConfirmed && paymentVerification.status === "unavailable" && <p className="checkin-error-block">{t("payment.verificationUnavailable")}</p>}
          {!paymentConfirmed && paymentVerification.status === "timed_out" && <p className="checkin-error-block">{t("payment.pollTimeout")}</p>}
          {!paymentConfirmed && paymentVerification.status === "error" && <p className="checkin-error-block">{t("payment.verificationStopped")}</p>}
          {paymentConfirmed && <p className="checkin-payment-confirmed" role="status">{t(paymentBypassActive ? "payment.testOverride" : "payment.confirmed")}</p>}
          <div className="bottom-actions">
            {paymentData?.required === true && !verifyingReturnedPayment && <button className="checkin-button primary" type="button" disabled={saving || (paymentData.can_request_payment_link === false && !paymentData.payment_link_url)} onClick={() => payBalance()}>{saving ? t("checking") : t("payment.pay")}</button>}
            {verifyingReturnedPayment && paymentVerification.status === "timed_out" && <button className="checkin-button primary" type="button" disabled={saving} onClick={checkPaymentAgain}>{t("payment.checkAgain")}</button>}
            {paymentConfirmed && <button className="checkin-button primary" type="button" disabled={saving} onClick={handleComplete}>{saving ? t("checking") : t("complete")}</button>}
            <button className="checkin-link-button" type="button" disabled={saving} onClick={() => setStep(hasServiceSummary ? "serviceSummary" : "review")}>{t("back")}</button>
          </div>
        </section>
      )}

    </main>
  );
};

const Header = ({ language, setLanguage, t }) => (
  <header className="checkin-header">
    <div className="checkin-wordmark">{t("wordmark")}</div>
    <label>
      <span className="visually-hidden">{t("language")}</span>
      <select value={language} onChange={(event) => setLanguage(event.target.value)}>
        <option value="de">DE</option>
        <option value="en">EN</option>
      </select>
    </label>
  </header>
);

const FormInput = ({ label, value, onChange, error, errorTarget, type = "text" }) => (
  <label className={`form-field ${error ? "has-error" : ""}`} data-error-target={errorTarget}>
    <span>{label}</span>
    <input type={type} value={value || ""} onChange={(event) => onChange(event.target.value)} />
    {error && <small>{error}</small>}
  </label>
);

const FormSelect = ({ label, value, onChange, options, error, errorTarget, placeholder }) => (
  <label className={`form-field ${error ? "has-error" : ""}`} data-error-target={errorTarget}>
    <span>{label}</span>
    <select value={value || ""} onChange={(event) => onChange(event.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    {error && <small>{error}</small>}
  </label>
);

const QuarterHourTimeInput = ({ label, hourLabel, minuteLabel, value, onChange, errorTarget }) => {
  const [hour = "", minute = "00"] = String(value || "").split(":");
  return (
    <fieldset className="checkin-time-field" data-error-target={errorTarget}>
      <legend>{label}</legend>
      <div className="checkin-time-selects">
        <label className="form-field">
          <span>{hourLabel}</span>
          <select value={hour} onChange={(event) => onChange(event.target.value ? `${event.target.value}:${minute}` : "")}>
            <option value="">--</option>
            {Array.from({ length: 24 }, (_, index) => String(index).padStart(2, "0")).map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </label>
        <label className="form-field">
          <span>{minuteLabel}</span>
          <select value={minute} disabled={!hour} onChange={(event) => onChange(`${hour}:${event.target.value}`)}>
            {["00", "15", "30", "45"].map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </label>
      </div>
    </fieldset>
  );
};

const ConsentRow = ({ label, checked, onChange, error }) => (
  <label className={`consent-row ${error ? "has-error" : ""}`}>
    <input type="checkbox" checked={Boolean(checked)} onChange={(event) => onChange(event.target.checked)} />
    <span>{label}</span>
    {error && <small>{error}</small>}
  </label>
);

const ServiceStatus = ({ label }) => <span className="checkin-service-status">{label}</span>;

const ReviewLine = ({ label, value }) => (
  <div className="review-line">
    <span>{label}</span>
    <strong>{value || "-"}</strong>
  </div>
);

export default SelfCheckin;
